/**
 * Trinity Playground - Main Entry
 *
 * Launcher page with animated previews for each triangle system.
 */

import { BackgroundSystem } from '@trinity/background-system';
import { CrystalField } from './core/crystal-field';
import { TRINITY_PALETTE } from './core/types';

// Background animation (subtle, behind content)
const bgCanvas = document.getElementById('bg-canvas') as HTMLCanvasElement;
if (bgCanvas) {
  const bg = new BackgroundSystem(bgCanvas, {
    sideLength: 60,
    animation: 'breathe',
    animationConfig: {
      speed: 0.3,
      intensity: 0.3,
    },
    baseOpacity: 0.4,
    backgroundColor: '#0a0f1a',
  });
}

// Mini preview renderers for cards
class MiniPreview {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private animationId: number | null = null;
  private startTime = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get 2D context');
    this.ctx = ctx;
    this.resize();
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.ctx.scale(dpr, dpr);
  }

  start(renderFn: (ctx: CanvasRenderingContext2D, time: number, w: number, h: number) => void): void {
    this.startTime = performance.now();

    const animate = () => {
      const time = (performance.now() - this.startTime) / 1000;
      const rect = this.canvas.getBoundingClientRect();
      renderFn(this.ctx, time, rect.width, rect.height);
      this.animationId = requestAnimationFrame(animate);
    };

    animate();
  }

  stop(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}

// Basics Preview - Crystal diamond field with shimmer
const basicsCanvas = document.getElementById('preview-basics') as HTMLCanvasElement;
if (basicsCanvas) {
  new CrystalField(basicsCanvas).start();
}

// Tri-Cube Preview - Isometric cube on animated triangle field
const trinityCanvas = document.getElementById('preview-tri-cube') as HTMLCanvasElement;
if (trinityCanvas) {
  const preview = new MiniPreview(trinityCanvas);
  const colors = [TRINITY_PALETTE.lightBlue, TRINITY_PALETTE.mediumBlue, TRINITY_PALETTE.darkBlue, TRINITY_PALETTE.deepBlue];

  const tcHash = (a: number, b: number) => {
    const x = Math.sin(a * 73.17 + b * 259.3) * 43758.5453;
    return x - Math.floor(x);
  };

  preview.start((ctx, time, w, h) => {
    // Background
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, '#080d18');
    bg.addColorStop(1, '#0c1222');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;

    // --- Layer 1: Animated tri-grid background ---
    const gs = 12;
    const gh = gs * Math.sqrt(3) / 2;
    const gridCols = Math.ceil(w / (gs / 2)) + 4;
    const gridRows = Math.ceil(h / gh) + 4;

    for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        const isUp = (r + c) % 2 === 0;
        const gx = -gs + c * (gs / 2);
        const gy = -gh + r * gh;
        const gcx = gx + gs / 2;
        const gcy = gy + gh / 2;

        // Distance from center
        const dx = (gcx - cx) / w;
        const dy = (gcy - cy) / h;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Two concentric waves rippling outward
        const wave1 = Math.sin(dist * 16 - time * 1.2) * 0.5 + 0.5;
        const wave2 = Math.sin(dist * 10 - time * 0.8 + 2.0) * 0.5 + 0.5;
        const wave = wave1 * 0.6 + wave2 * 0.4;
        const rnd = tcHash(r, c);

        if (wave * rnd < 0.2) continue;

        // 3-axis coloring like tri-cube coordinates
        const a = r + 1;
        const b = isUp ? c : c + 1;
        const m = Math.max(a % 3, b % 3, (a + b) % 3);
        const colorIdx = m === (a % 3) ? 0 : m === (b % 3) ? 1 : 2;

        ctx.fillStyle = colors[colorIdx];
        ctx.globalAlpha = 0.04 + wave * rnd * 0.14;

        ctx.beginPath();
        if (isUp) {
          ctx.moveTo(gx + gs / 2, gy);
          ctx.lineTo(gx, gy + gh);
          ctx.lineTo(gx + gs, gy + gh);
        } else {
          ctx.moveTo(gx + gs / 2, gy + gh);
          ctx.lineTo(gx + gs, gy);
          ctx.lineTo(gx, gy);
        }
        ctx.closePath();
        ctx.fill();
      }
    }

    // --- Layer 2: Isometric cube (3 rhombuses, each = 2 triangles) ---
    // Cube sits at center, gently breathing in size
    const breath = 1.0 + Math.sin(time * 0.6) * 0.04;
    const S = 36 * breath; // side length
    const H = S * Math.sqrt(3) / 2; // triangle height

    // Isometric cube vertices:
    //        top
    //       / | \
    //     tl  |  tr
    //      \  |  /
    //       mid (center of hex)
    //      / | \  -- actually let me think isometric:
    //
    // Classic isometric cube: 3 rhombuses meeting at center
    // Top vertex, left vertex, right vertex, bottom vertex, and two mid vertices
    const top = { x: cx, y: cy - H };
    const left = { x: cx - S, y: cy };
    const right = { x: cx + S, y: cy };
    const bottom = { x: cx, y: cy + H };
    const midL = { x: cx - S / 2, y: cy - H / 2 };  // actually no...

    // Better: standard isometric cube
    // The cube has 6 visible vertices. Center is the front corner.
    // 3 faces: top, left, right
    const center = { x: cx, y: cy };
    const topV = { x: cx, y: cy - S * 1.15 };
    const topLeft = { x: cx - S, y: cy - S * 0.575 };
    const topRight = { x: cx + S, y: cy - S * 0.575 };
    const botLeft = { x: cx - S, y: cy + S * 0.575 };
    const botRight = { x: cx + S, y: cy + S * 0.575 };
    const bottomV = { x: cx, y: cy + S * 1.15 };

    // Draw faces back-to-front

    // Top face (lightest) — lightBlue
    const topPulse = 0.7 + Math.sin(time * 1.2) * 0.15;
    ctx.globalAlpha = topPulse;
    ctx.fillStyle = colors[0]; // lightBlue
    ctx.beginPath();
    ctx.moveTo(topV.x, topV.y);
    ctx.lineTo(topLeft.x, topLeft.y);
    ctx.lineTo(center.x, center.y);
    ctx.lineTo(topRight.x, topRight.y);
    ctx.closePath();
    ctx.fill();
    // Subtle brighter triangle for top-right half
    ctx.fillStyle = '#2bc5f5';
    ctx.globalAlpha = topPulse * 0.25;
    ctx.beginPath();
    ctx.moveTo(topV.x, topV.y);
    ctx.lineTo(center.x, center.y);
    ctx.lineTo(topRight.x, topRight.y);
    ctx.closePath();
    ctx.fill();

    // Left face (medium) — darkBlue
    const leftPulse = 0.65 + Math.sin(time * 1.2 + 2.1) * 0.15;
    ctx.globalAlpha = leftPulse;
    ctx.fillStyle = colors[2]; // darkBlue
    ctx.beginPath();
    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(botLeft.x, botLeft.y);
    ctx.lineTo(bottomV.x, bottomV.y);
    ctx.lineTo(center.x, center.y);
    ctx.closePath();
    ctx.fill();
    // Darker lower triangle
    ctx.fillStyle = colors[3]; // deepBlue
    ctx.globalAlpha = leftPulse * 0.35;
    ctx.beginPath();
    ctx.moveTo(botLeft.x, botLeft.y);
    ctx.lineTo(bottomV.x, bottomV.y);
    ctx.lineTo(center.x, center.y);
    ctx.closePath();
    ctx.fill();

    // Right face (medium-dark) — mediumBlue
    const rightPulse = 0.6 + Math.sin(time * 1.2 + 4.2) * 0.15;
    ctx.globalAlpha = rightPulse;
    ctx.fillStyle = colors[1]; // mediumBlue
    ctx.beginPath();
    ctx.moveTo(topRight.x, topRight.y);
    ctx.lineTo(center.x, center.y);
    ctx.lineTo(bottomV.x, bottomV.y);
    ctx.lineTo(botRight.x, botRight.y);
    ctx.closePath();
    ctx.fill();
    // Darker lower triangle
    ctx.fillStyle = colors[2]; // darkBlue
    ctx.globalAlpha = rightPulse * 0.3;
    ctx.beginPath();
    ctx.moveTo(center.x, center.y);
    ctx.lineTo(bottomV.x, bottomV.y);
    ctx.lineTo(botRight.x, botRight.y);
    ctx.closePath();
    ctx.fill();

    // Edge highlights
    ctx.globalAlpha = 0.4;
    ctx.strokeStyle = colors[0];
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    // Top edges
    ctx.moveTo(topLeft.x, topLeft.y);
    ctx.lineTo(topV.x, topV.y);
    ctx.lineTo(topRight.x, topRight.y);
    // Center spine
    ctx.moveTo(topV.x, topV.y);
    ctx.lineTo(center.x, center.y);
    ctx.lineTo(bottomV.x, bottomV.y);
    ctx.stroke();

    // Front vertex glow
    const glowPulse = 0.3 + Math.sin(time * 2) * 0.2;
    const glow = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, S * 0.4);
    glow.addColorStop(0, `rgba(27, 177, 231, ${glowPulse})`);
    glow.addColorStop(1, 'rgba(27, 177, 231, 0)');
    ctx.globalAlpha = 1;
    ctx.fillStyle = glow;
    ctx.fillRect(center.x - S, center.y - S, S * 2, S * 2);

    // --- Layer 3: Subtle ambient radial glow ---
    const ambient = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.5);
    ambient.addColorStop(0, 'rgba(0, 96, 175, 0.06)');
    ambient.addColorStop(0.6, 'rgba(20, 63, 145, 0.03)');
    ambient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = ambient;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = 1;
  });
}

// Assembly Preview - Rotating logo shape
const assemblyCanvas = document.getElementById('preview-assembly') as HTMLCanvasElement;
if (assemblyCanvas) {
  const preview = new MiniPreview(assemblyCanvas);

  preview.start((ctx, time, w, h) => {
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, w, h);

    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(time * 0.2);

    const size = 25;
    const height = size * Math.sqrt(3) / 2;

    // Draw 3 rhombuses in a trinity pattern
    const drawRhombus = (offsetX: number, offsetY: number, rotation: number, color1: string, color2: string) => {
      ctx.save();
      ctx.translate(offsetX, offsetY);
      ctx.rotate(rotation);

      // Up triangle
      ctx.fillStyle = color1;
      ctx.beginPath();
      ctx.moveTo(0, -height);
      ctx.lineTo(-size / 2, 0);
      ctx.lineTo(size / 2, 0);
      ctx.closePath();
      ctx.fill();

      // Down triangle
      ctx.fillStyle = color2;
      ctx.beginPath();
      ctx.moveTo(0, height);
      ctx.lineTo(size / 2, 0);
      ctx.lineTo(-size / 2, 0);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    };

    // Three arms
    drawRhombus(0, -height * 1.5, 0, TRINITY_PALETTE.lightBlue, TRINITY_PALETTE.mediumBlue);
    drawRhombus(height * 1.3, height * 0.75, Math.PI * 2 / 3, TRINITY_PALETTE.mediumBlue, TRINITY_PALETTE.darkBlue);
    drawRhombus(-height * 1.3, height * 0.75, -Math.PI * 2 / 3, TRINITY_PALETTE.darkBlue, TRINITY_PALETTE.deepBlue);

    ctx.restore();
  });
}

// Background Preview - Animated swarm
const backgroundCanvas = document.getElementById('preview-background') as HTMLCanvasElement;
if (backgroundCanvas) {
  const preview = new MiniPreview(backgroundCanvas);
  const colors = [TRINITY_PALETTE.lightBlue, TRINITY_PALETTE.mediumBlue, TRINITY_PALETTE.darkBlue];

  // Simple noise function for preview
  const noise = (x: number, y: number, t: number) => {
    return Math.sin(x * 3 + t) * Math.cos(y * 3 + t * 0.7) * 0.5 + 0.5;
  };

  preview.start((ctx, time, w, h) => {
    ctx.fillStyle = '#0a0f1a';
    ctx.fillRect(0, 0, w, h);

    const size = 18;
    const triH = size * Math.sqrt(3) / 2;
    const cols = Math.ceil(w / triH) + 2;
    const rows = Math.ceil(h / (size / 2)) + 2;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const isUp = (row + col) % 2 === 0;
        const x = col * triH;
        const y = row * (size / 2);

        const nx = x / w;
        const ny = y / h;
        const n = noise(nx * 4, ny * 4, time);

        const scale = 0.3 + n * 0.7;
        const opacity = 0.3 + n * 0.5;

        const colorIdx = Math.floor(n * colors.length) % colors.length;
        ctx.fillStyle = colors[colorIdx];
        ctx.globalAlpha = opacity;

        const cx = x + triH / 2;
        const cy = y + size / 4;
        const scaledSize = size * scale;
        const scaledTriH = triH * scale;

        ctx.beginPath();
        if (isUp) {
          ctx.moveTo(cx - scaledTriH / 2, cy);
          ctx.lineTo(cx + scaledTriH / 2, cy - scaledSize / 2);
          ctx.lineTo(cx + scaledTriH / 2, cy + scaledSize / 2);
        } else {
          ctx.moveTo(cx + scaledTriH / 2, cy);
          ctx.lineTo(cx - scaledTriH / 2, cy + scaledSize / 2);
          ctx.lineTo(cx - scaledTriH / 2, cy - scaledSize / 2);
        }
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  });
}

// Swarm Preview - Particle simulation
const swarmCanvas = document.getElementById('preview-swarm') as HTMLCanvasElement;
if (swarmCanvas) {
  const preview = new MiniPreview(swarmCanvas);

  // Simple particle swarm state
  const NUM_PREVIEW_PARTICLES = 60;
  const swarmParticles: { x: number; y: number; vx: number; vy: number }[] = [];
  for (let i = 0; i < NUM_PREVIEW_PARTICLES; i++) {
    swarmParticles.push({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.01,
      vy: (Math.random() - 0.5) * 0.01,
    });
  }

  preview.start((ctx, time, w, h) => {
    ctx.fillStyle = '#0a0f1a';
    ctx.fillRect(0, 0, w, h);

    // Simple flocking: steer toward center + alignment
    const cx = swarmParticles.reduce((s, p) => s + p.x, 0) / NUM_PREVIEW_PARTICLES;
    const cy = swarmParticles.reduce((s, p) => s + p.y, 0) / NUM_PREVIEW_PARTICLES;

    for (const p of swarmParticles) {
      // Cohesion toward center
      p.vx += (cx - p.x) * 0.0005;
      p.vy += (cy - p.y) * 0.0005;
      // Spiral force
      p.vx += (cy - p.y) * 0.0003;
      p.vy -= (cx - p.x) * 0.0003;
      // Noise
      p.vx += (Math.random() - 0.5) * 0.0008;
      p.vy += (Math.random() - 0.5) * 0.0008;
      // Friction
      p.vx *= 0.98;
      p.vy *= 0.98;
      // Move
      p.x += p.vx;
      p.y += p.vy;
      // Boundary wrap
      if (p.x < 0) p.x += 1;
      if (p.x > 1) p.x -= 1;
      if (p.y < 0) p.y += 1;
      if (p.y > 1) p.y -= 1;

      // Draw as tiny triangle
      const px = p.x * w;
      const py = p.y * h;
      const angle = Math.atan2(p.vy, p.vx);
      const size = 4;

      const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      const t = Math.min(speed / 0.015, 1);
      ctx.fillStyle = t > 0.5 ? TRINITY_PALETTE.lightBlue : TRINITY_PALETTE.mediumBlue;
      ctx.globalAlpha = 0.7 + t * 0.3;

      ctx.beginPath();
      ctx.moveTo(px + Math.cos(angle) * size, py + Math.sin(angle) * size);
      ctx.lineTo(px + Math.cos(angle + 2.4) * size * 0.6, py + Math.sin(angle + 2.4) * size * 0.6);
      ctx.lineTo(px + Math.cos(angle - 2.4) * size * 0.6, py + Math.sin(angle - 2.4) * size * 0.6);
      ctx.closePath();
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  });
}

// WebGL Renderer Preview - Shaders 1,3,5 each taking 1/3 of the card
const webglCanvas = document.getElementById('preview-webgl') as HTMLCanvasElement;
if (webglCanvas) {
  const preview = new MiniPreview(webglCanvas);

  // Create offscreen WebGL canvas for shader rendering
  const glCanvas = document.createElement('canvas');
  const glCtx = glCanvas.getContext('webgl', { antialias: false, alpha: false });

  if (glCtx) {
    // Import shader sources inline (simplified versions for preview)
    const VERT = `attribute vec2 a_position;varying vec2 v_uv;void main(){v_uv=a_position*0.5+0.5;gl_Position=vec4(a_position,0.0,1.0);}`;

    const GLSL_HEAD = `precision highp float;varying vec2 v_uv;uniform float u_time;uniform vec2 u_resolution;
#define PI 3.14159265359
#define SQRT3 1.7320508
float hash(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float noise(vec2 p){vec2 i=floor(p);vec2 f=fract(p);f=f*f*(3.0-2.0*f);float a=hash(i);float b=hash(i+vec2(1.0,0.0));float c=hash(i+vec2(0.0,1.0));float d=hash(i+vec2(1.0,1.0));return mix(mix(a,b,f.x),mix(c,d,f.x),f.y);}
`;

    // Shader 1: Trinity Gradient
    const FRAG_GRADIENT = GLSL_HEAD + `void main(){
vec2 uv=v_uv;float aspect=u_resolution.x/u_resolution.y;vec2 p=vec2(uv.x*aspect,uv.y);
float scale=20.0;float h=SQRT3*0.5;vec2 gp=p*scale;
float row=floor(gp.y/h);float col=floor(gp.x+mod(row,2.0)*0.5);float id=hash(vec2(col,row));
vec3 lightBlue=vec3(0.106,0.694,0.906);vec3 medBlue=vec3(0.0,0.6,0.855);vec3 darkBlue=vec3(0.0,0.376,0.686);vec3 deepBlue=vec3(0.078,0.247,0.569);
float t=uv.x*0.5+uv.y*0.3+sin(u_time*0.5+id*6.28)*0.15;t=clamp(t,0.0,1.0);
vec3 c;if(t<0.33)c=mix(deepBlue,darkBlue,t/0.33);else if(t<0.66)c=mix(darkBlue,medBlue,(t-0.33)/0.33);else c=mix(medBlue,lightBlue,(t-0.66)/0.34);
vec2 lc=fract(gp/vec2(1.0,h));float edge=smoothstep(0.0,0.05,min(lc.x,min(lc.y,1.0-lc.x)));
c*=0.7+0.3*edge;c+=0.02*sin(u_time+id*20.0);
gl_FragColor=vec4(c,1.0);}`;

    // Shader 3: Thermal Field
    const FRAG_THERMAL = GLSL_HEAD + `void main(){
vec2 uv=v_uv;float aspect=u_resolution.x/u_resolution.y;vec2 p=vec2(uv.x*aspect,uv.y);
float scale=18.0;float h=SQRT3*0.5;vec2 gp=p*scale;
float row=floor(gp.y/h);float col=floor(gp.x+mod(row,2.0)*0.5);float id=hash(vec2(col,row));
float temp=0.0;temp+=noise(p*3.0+u_time*0.3)*0.5;temp+=noise(p*6.0-u_time*0.2)*0.25;temp+=noise(p*12.0+u_time*0.1)*0.125;temp+=id*0.1;
temp=clamp(temp,0.0,1.0);
vec3 c;if(temp<0.2)c=mix(vec3(0.0,0.0,0.1),vec3(0.0,0.0,0.8),temp/0.2);else if(temp<0.4)c=mix(vec3(0.0,0.0,0.8),vec3(0.8,0.0,0.6),(temp-0.2)/0.2);else if(temp<0.6)c=mix(vec3(0.8,0.0,0.6),vec3(1.0,0.2,0.0),(temp-0.4)/0.2);else if(temp<0.8)c=mix(vec3(1.0,0.2,0.0),vec3(1.0,0.9,0.0),(temp-0.6)/0.2);else c=mix(vec3(1.0,0.9,0.0),vec3(1.0,1.0,0.9),(temp-0.8)/0.2);
vec2 lc=fract(gp/vec2(1.0,h));float edge=smoothstep(0.0,0.03,min(lc.x,min(lc.y,1.0-lc.x)));
c*=0.85+0.15*edge;
gl_FragColor=vec4(c,1.0);}`;

    // Shader 5: Wave Field
    const FRAG_WAVE = GLSL_HEAD + `void main(){
vec2 uv=v_uv;float aspect=u_resolution.x/u_resolution.y;vec2 p=vec2(uv.x*aspect,uv.y);
float scale=22.0;float h=SQRT3*0.5;vec2 gp=p*scale;
float row=floor(gp.y/h);float col=floor(gp.x+mod(row,2.0)*0.5);float id=hash(vec2(col,row));float isUp=mod(col+row,2.0);
float w1=sin(length(p-vec2(0.2,0.3))*12.0-u_time*2.5);float w2=sin(length(p-vec2(aspect-0.3,0.7))*10.0-u_time*2.0);float w3=sin(p.x*6.0+p.y*4.0-u_time*1.5);
float wave=(w1+w2+w3)/3.0;float hv=wave*0.5+0.5+id*0.08;
vec3 deep=vec3(0.01,0.04,0.15);vec3 mid=vec3(0.0,0.2,0.5);vec3 bright=vec3(0.1,0.7,0.9);vec3 foam=vec3(0.8,0.95,1.0);
vec3 c;if(hv<0.3)c=mix(deep,mid,hv/0.3);else if(hv<0.6)c=mix(mid,bright,(hv-0.3)/0.3);else c=mix(bright,foam,(hv-0.6)/0.4);
c*=0.9+0.1*isUp;
vec2 lc=fract(gp/vec2(1.0,h));float edge=smoothstep(0.0,0.025,min(lc.x,min(lc.y,1.0-lc.x)));
c*=0.85+0.15*edge;
gl_FragColor=vec4(c,1.0);}`;

    const frags = [FRAG_GRADIENT, FRAG_THERMAL, FRAG_WAVE];

    // Compile shaders
    function compileGL(src: string, type: number): WebGLShader | null {
      const s = glCtx!.createShader(type);
      if (!s) return null;
      glCtx!.shaderSource(s, src);
      glCtx!.compileShader(s);
      if (!glCtx!.getShaderParameter(s, glCtx!.COMPILE_STATUS)) {
        console.warn('Preview shader error:', glCtx!.getShaderInfoLog(s));
        glCtx!.deleteShader(s);
        return null;
      }
      return s;
    }

    function linkGL(fragSrc: string): WebGLProgram | null {
      const vs = compileGL(VERT, glCtx!.VERTEX_SHADER);
      const fs = compileGL(fragSrc, glCtx!.FRAGMENT_SHADER);
      if (!vs || !fs) return null;
      const prog = glCtx!.createProgram()!;
      glCtx!.attachShader(prog, vs);
      glCtx!.attachShader(prog, fs);
      glCtx!.linkProgram(prog);
      glCtx!.deleteShader(vs);
      glCtx!.deleteShader(fs);
      if (!glCtx!.getProgramParameter(prog, glCtx!.LINK_STATUS)) {
        console.warn('Preview link error:', glCtx!.getProgramInfoLog(prog));
        return null;
      }
      return prog;
    }

    const programs = frags.map(f => linkGL(f));
    const quadBuf = glCtx.createBuffer()!;
    glCtx.bindBuffer(glCtx.ARRAY_BUFFER, quadBuf);
    glCtx.bufferData(glCtx.ARRAY_BUFFER, new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]), glCtx.STATIC_DRAW);

    const startTime = performance.now();

    preview.start((ctx, _time, w, h) => {
      const time = (performance.now() - startTime) / 1000;
      const thirdW = Math.ceil(w / 3);

      // Render each shader into the offscreen GL canvas at 1/3 width
      glCanvas.width = thirdW * (window.devicePixelRatio || 1);
      glCanvas.height = h * (window.devicePixelRatio || 1);

      for (let i = 0; i < 3; i++) {
        const prog = programs[i];
        if (!prog) continue;

        glCtx!.viewport(0, 0, glCanvas.width, glCanvas.height);
        glCtx!.useProgram(prog);

        const aPos = glCtx!.getAttribLocation(prog, 'a_position');
        glCtx!.bindBuffer(glCtx!.ARRAY_BUFFER, quadBuf);
        glCtx!.enableVertexAttribArray(aPos);
        glCtx!.vertexAttribPointer(aPos, 2, glCtx!.FLOAT, false, 0, 0);

        const uTime = glCtx!.getUniformLocation(prog, 'u_time');
        const uRes = glCtx!.getUniformLocation(prog, 'u_resolution');
        if (uTime) glCtx!.uniform1f(uTime, time);
        if (uRes) glCtx!.uniform2f(uRes, glCanvas.width, glCanvas.height);

        glCtx!.drawArrays(glCtx!.TRIANGLES, 0, 6);

        // Copy to 2D canvas at the correct 1/3 slice
        ctx.drawImage(glCanvas, i * thirdW, 0, thirdW, h);
      }

      // Subtle divider lines between the three sections
      ctx.strokeStyle = 'rgba(255,255,255,0.15)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(thirdW, 0); ctx.lineTo(thirdW, h);
      ctx.moveTo(thirdW * 2, 0); ctx.lineTo(thirdW * 2, h);
      ctx.stroke();
    });
  }
}

// Wireframe Gallery Preview — rotating torus + diamond lattice
const wireframesCanvas = document.getElementById('preview-wireframes') as HTMLCanvasElement;
if (wireframesCanvas) {
  const preview = new MiniPreview(wireframesCanvas);
  const colors = [TRINITY_PALETTE.lightBlue, TRINITY_PALETTE.mediumBlue, TRINITY_PALETTE.darkBlue, TRINITY_PALETTE.deepBlue];

  const wfHash = (a: number, b: number) => {
    const x = Math.sin(a * 173.7 + b * 237.1) * 43758.5453;
    return x - Math.floor(x);
  };

  // 3D projection helpers
  const rotateY = (x: number, y: number, z: number, a: number): [number, number, number] => {
    const c = Math.cos(a), s = Math.sin(a);
    return [x * c + z * s, y, -x * s + z * c];
  };
  const rotateX = (x: number, y: number, z: number, a: number): [number, number, number] => {
    const c = Math.cos(a), s = Math.sin(a);
    return [x, y * c - z * s, y * s + z * c];
  };
  const project = (x: number, y: number, z: number, fov: number): [number, number, number] => {
    const d = fov / (fov + z);
    return [x * d, y * d, d];
  };

  // Generate torus vertices (major R, minor r)
  function torusPoint(R: number, r: number, u: number, v: number): [number, number, number] {
    const cu = Math.cos(u), su = Math.sin(u), cv = Math.cos(v), sv = Math.sin(v);
    return [(R + r * cv) * cu, (R + r * cv) * su, r * sv];
  }

  // Generate diamond lattice vertices (FCC with two-atom basis)
  function diamondLattice(size: number, spacing: number): [number, number, number][] {
    const pts: [number, number, number][] = [];
    const half = (size - 1) * spacing / 2;
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        for (let k = 0; k < size; k++) {
          const bx = i * spacing - half;
          const by = j * spacing - half;
          const bz = k * spacing - half;
          // FCC basis: 4 atoms
          pts.push([bx, by, bz]);
          pts.push([bx + spacing / 2, by + spacing / 2, bz]);
          pts.push([bx + spacing / 2, by, bz + spacing / 2]);
          pts.push([bx, by + spacing / 2, bz + spacing / 2]);
          // Second basis offset by (1/4, 1/4, 1/4)
          const o = spacing / 4;
          pts.push([bx + o, by + o, bz + o]);
          pts.push([bx + o + spacing / 2, by + o + spacing / 2, bz + o]);
          pts.push([bx + o + spacing / 2, by + o, bz + o + spacing / 2]);
          pts.push([bx + o, by + o + spacing / 2, bz + o + spacing / 2]);
        }
      }
    }
    return pts;
  }

  // Find diamond bonds (nearest neighbors at distance spacing*sqrt(3)/4)
  function diamondBonds(pts: [number, number, number][], spacing: number): [number, number][] {
    const bonds: [number, number][] = [];
    const bondLen = spacing * Math.sqrt(3) / 4;
    const tol = bondLen * 1.1;
    const tolSq = tol * tol;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i][0] - pts[j][0];
        const dy = pts[i][1] - pts[j][1];
        const dz = pts[i][2] - pts[j][2];
        const dSq = dx * dx + dy * dy + dz * dz;
        if (dSq < tolSq && dSq > 0.01) {
          bonds.push([i, j]);
        }
      }
    }
    return bonds;
  }

  const latticeSpacing = 66;
  const latticePts = diamondLattice(2, latticeSpacing);
  const latticeBonds = diamondBonds(latticePts, latticeSpacing);

  preview.start((ctx, time, w, h) => {
    // Background
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, '#080d18');
    bg.addColorStop(0.5, '#0c1222');
    bg.addColorStop(1, '#0a0f1a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;

    // --- Background layer: triangle grid with wave shimmer ---
    const gs = 14;
    const gh = gs * Math.sqrt(3) / 2;
    const gridCols = Math.ceil(w / (gs / 2)) + 4;
    const gridRows = Math.ceil(h / gh) + 4;
    const gox = -gs;
    const goy = -gh;

    for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        const isUp = (r + c) % 2 === 0;
        const gx = gox + c * (gs / 2);
        const gy = goy + r * gh;

        const dx = (gx + gs / 2 - cx) / w;
        const dy = (gy + gh / 2 - cy) / h;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const wave = Math.sin(dist * 12 - time * 1.5) * 0.5 + 0.5;
        const flicker = wfHash(r, c);

        if (wave * flicker < 0.25) continue;

        const colorIdx = (r + c + Math.floor(flicker * 4)) % colors.length;
        ctx.fillStyle = colors[colorIdx];
        ctx.globalAlpha = 0.06 + wave * flicker * 0.12;

        ctx.beginPath();
        if (isUp) {
          ctx.moveTo(gx + gs / 2, gy);
          ctx.lineTo(gx, gy + gh);
          ctx.lineTo(gx + gs, gy + gh);
        } else {
          ctx.moveTo(gx + gs / 2, gy + gh);
          ctx.lineTo(gx + gs, gy);
          ctx.lineTo(gx, gy);
        }
        ctx.closePath();
        ctx.fill();
      }
    }

    const halfW = w / 2;
    const fov = 200;

    // === Left half: Torus ===
    const torusCx = halfW * 0.5;
    const torusCy = h / 2;
    const R = 28, r = 12;
    const uSegs = 20, vSegs = 12;
    const rotY = time * 0.4;
    const rotXa = time * 0.25 + 0.5;

    // Draw torus wireframe
    ctx.strokeStyle = colors[0];
    ctx.lineWidth = 0.8;

    for (let i = 0; i < uSegs; i++) {
      for (let j = 0; j < vSegs; j++) {
        const u0 = (i / uSegs) * Math.PI * 2;
        const u1 = ((i + 1) / uSegs) * Math.PI * 2;
        const v0 = (j / vSegs) * Math.PI * 2;
        const v1 = ((j + 1) / vSegs) * Math.PI * 2;

        const transform = (p: [number, number, number]): [number, number, number] => {
          let pt = rotateY(p[0], p[1], p[2], rotY);
          pt = rotateX(pt[0], pt[1], pt[2], rotXa);
          return pt;
        };

        const p00 = transform(torusPoint(R, r, u0, v0));
        const p10 = transform(torusPoint(R, r, u1, v0));
        const p01 = transform(torusPoint(R, r, u0, v1));

        const pr00 = project(p00[0], p00[1], p00[2], fov);
        const pr10 = project(p10[0], p10[1], p10[2], fov);
        const pr01 = project(p01[0], p01[1], p01[2], fov);

        // Depth-based opacity
        const depth = (p00[2] + 60) / 120;
        ctx.globalAlpha = 0.15 + depth * 0.5;
        ctx.strokeStyle = depth > 0.5 ? colors[0] : colors[2];

        // Ring line (u direction)
        ctx.beginPath();
        ctx.moveTo(torusCx + pr00[0], torusCy + pr00[1]);
        ctx.lineTo(torusCx + pr10[0], torusCy + pr10[1]);
        ctx.stroke();

        // Tube line (v direction)
        ctx.beginPath();
        ctx.moveTo(torusCx + pr00[0], torusCy + pr00[1]);
        ctx.lineTo(torusCx + pr01[0], torusCy + pr01[1]);
        ctx.stroke();
      }
    }

    // === Right half: Diamond Lattice ===
    const latCx = halfW + halfW * 0.5;
    const latCy = h / 2;
    const latRotY = time * 0.3;
    const latRotX = time * 0.2 + 0.3;

    // Transform and project all points
    const projected = latticePts.map((p) => {
      let pt = rotateY(p[0], p[1], p[2], latRotY);
      pt = rotateX(pt[0], pt[1], pt[2], latRotX);
      const pr = project(pt[0], pt[1], pt[2], fov);
      return { x: pr[0], y: pr[1], depth: pt[2] };
    });

    // Draw bonds
    ctx.lineWidth = 0.8;
    for (const [i, j] of latticeBonds) {
      const a = projected[i];
      const b = projected[j];
      const avgDepth = (a.depth + b.depth) / 2;
      const norm = (avgDepth + 40) / 80;
      ctx.globalAlpha = 0.15 + Math.max(0, norm) * 0.55;
      ctx.strokeStyle = norm > 0.5 ? colors[1] : colors[3];
      ctx.beginPath();
      ctx.moveTo(latCx + a.x, latCy + a.y);
      ctx.lineTo(latCx + b.x, latCy + b.y);
      ctx.stroke();
    }

    // Draw nodes
    for (const p of projected) {
      const norm = (p.depth + 40) / 80;
      const sz = 1.2 + Math.max(0, norm) * 1.8;
      ctx.globalAlpha = 0.3 + Math.max(0, norm) * 0.6;
      ctx.fillStyle = norm > 0.6 ? colors[0] : norm > 0.3 ? colors[1] : colors[2];
      ctx.beginPath();
      ctx.arc(latCx + p.x, latCy + p.y, sz, 0, Math.PI * 2);
      ctx.fill();
    }

    // Subtle divider line between the two halves
    ctx.globalAlpha = 0.1;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(halfW, 0);
    ctx.lineTo(halfW, h);
    ctx.stroke();

    ctx.globalAlpha = 1;
  });
}

// Keyboard navigation
document.addEventListener('keydown', (e) => {
  const cards = document.querySelectorAll('.game-card:not(.disabled)') as NodeListOf<HTMLAnchorElement>;

  if (e.key >= '1' && e.key <= '9') {
    const idx = parseInt(e.key, 10) - 1;
    if (idx < cards.length) {
      const href = cards[idx].getAttribute('href');
      if (href) {
        window.location.href = href;
      }
    }
  }
});

// Card hover sound effect (optional, can be enabled)
const cards = document.querySelectorAll('.game-card');
cards.forEach((card) => {
  card.addEventListener('mouseenter', () => {
    // Could add subtle audio feedback here
  });
});

console.log('🔺 Trinity Playground');
console.log('Press 1-3 to launch a system');
